import { useEffect, useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
  flexRender,
} from "@tanstack/react-table";
import { apiClient } from "../services/api";
import PageLayout from "../components/PageLayout";
import TableInput from "../components/TableInput";
import Dropdown from "../components/Dropdown";
import Badge from "../components/Badge";
import { GRADE_FILTER_OPTIONS, GRADE_ORDER } from "../components/Grade";
import {
  tanstackTableStyle,
  getTanstackThStyle,
  sortHeaderStyle,
  sortArrowStyle,
  filterContainerStyle,
  tanstackTrStyle,
  getTanstackTdStyle,
  emptyRowStyle,
} from "../components/Style";

type AttendanceRow = {
  id?: number;
  studentNumber: string;
  name: string;
  grade: string;
  class: string;
  attendance: string;
  createdAt: string;
};

const PAGE_SIZE = 30;

const columnHelper = createColumnHelper<AttendanceRow>();

function AttendancePage() {
  const [rows, setRows] = useState<AttendanceRow[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [page, setPage] = useState(1);

  // API에서 출결 이력 데이터 가져옴
  const fetchAttendanceHistory = async () => {
    try {
      const data = await apiClient.getAttendanceHistory({});
      setRows(data);
      setPage(1);
    } catch (error) {
      console.error("출결 이력 조회 실패:", error);
    }
  };

  // API에서 반 목록 가져오기
  const fetchClasses = async () => {
    try {
      const data = await apiClient.getClasses();
      setClasses(data);
    } catch (error) {
      console.error("반 목록 조회 실패:", error);
    }
  };

  useEffect(() => {
    fetchAttendanceHistory();
    fetchClasses();
  }, []);

  // 반 목록 - DB의 클래스 테이블에서 가져옴
  const classList = useMemo(() => {
    return classes.map((c) => c.className).sort();
  }, [classes]);

  // 출결 상태 렌더링 함수
  const renderAttendanceStatus = (status: string) => {
    const actualStatus = status || "미등록";
    const colorMap: Record<string, { backgroundColor: string; textColor: string; borderColor: string }> = {
      등원: { backgroundColor: "#f0f9f4", textColor: "#1f883d", borderColor: "#a4dbb5" },
      하원: { backgroundColor: "#f2f6ff", textColor: "#2466c2", borderColor: "#b6e3ff" },
      지각: { backgroundColor: "#fef3c7", textColor: "#d97706", borderColor: "#fcd34d" },
      결석: { backgroundColor: "#fee2e2", textColor: "#dc2626", borderColor: "#fca5a5" },
      미등록: { backgroundColor: "#f3f4f6", textColor: "#6b7280", borderColor: "#d1d5db" },
    };
    const colors = colorMap[actualStatus] || colorMap["미등록"];
    return (
      <Badge {...colors}>
        {actualStatus}
      </Badge>
    );
  };

  // 테이블 컬럼 정의
  const columns = useMemo(
    () => [
      columnHelper.accessor("studentNumber", {
        header: "출결번호",
        size: 100,
        cell: (info) => info.getValue(),
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("name", {
        header: "이름",
        size: 100,
        cell: (info) => info.getValue(),
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("grade", {
        header: "학년",
        size: 80,
        cell: (info) => info.getValue(),
        sortingFn: (rowA, rowB, columnId) => {
          const gradeA = GRADE_ORDER[rowA.getValue(columnId) as string] || 999;
          const gradeB = GRADE_ORDER[rowB.getValue(columnId) as string] || 999;
          if (gradeA !== gradeB) {
            return gradeA - gradeB;
          }
          return rowA.original.name.localeCompare(rowB.original.name);
        },
      }),
      columnHelper.accessor("class", {
        header: "반",
        size: 120,
        cell: (info) => info.getValue(),
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("attendance", {
        header: "출결상태",
        size: 100,
        cell: (info) => renderAttendanceStatus(info.getValue()),
        sortingFn: "alphanumeric",
      }),
      columnHelper.accessor("createdAt", {
        header: "일시",
        size: 220,
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleString("ko-KR", {
                hour12: false,
                timeZone: "Asia/Seoul",
              })
            : "",
        sortingFn: "datetime",
      }),
    ],
    []
  );

  // Table instance
  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  // 페이징 - 필터링/정렬 후 데이터 기준
  const filteredRows = table.getRowModel().rows;
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPages = Math.ceil(filteredRows.length / PAGE_SIZE);

  return (
    <PageLayout title="출결 이력">
      <style>
        {`
          .attendance-table tbody tr:hover {
            background-color: #f6f8fa;
          }
        `}
      </style>
      <div style={{ overflow: "auto", maxHeight: "calc(100vh - 180px)" }}>
        <table className="attendance-table" style={tanstackTableStyle}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnId = header.column.id;

                  return (
                    <th
                      key={header.id}
                      style={getTanstackThStyle(false, header.getSize(), {})}
                    >
                      {header.isPlaceholder ? null : (
                        <div>
                          {/* Header with sort */}
                          {header.column.getCanSort() ? (
                            <div
                              onClick={header.column.getToggleSortingHandler()}
                              style={sortHeaderStyle}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {{
                                asc: <span style={sortArrowStyle}>▲</span>,
                                desc: <span style={sortArrowStyle}>▼</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>
                          ) : (
                            flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          )}

                          {/* Filter row */}
                          <div style={filterContainerStyle}>
                            {columnId === "studentNumber" && (
                              <TableInput
                                value={
                                  (header.column.getFilterValue() as string) ?? ""
                                }
                                onChange={(e) =>
                                  header.column.setFilterValue(e.target.value)
                                }
                                placeholder="검색"
                              />
                            )}
                            {columnId === "name" && (
                              <TableInput
                                value={
                                  (header.column.getFilterValue() as string) ?? ""
                                }
                                onChange={(e) =>
                                  header.column.setFilterValue(e.target.value)
                                }
                                placeholder="검색"
                              />
                            )}
                            {columnId === "grade" && (
                              <Dropdown
                                value={
                                  (header.column.getFilterValue() as string) ?? ""
                                }
                                onChange={(value) =>
                                  header.column.setFilterValue(value || undefined)
                                }
                                options={[
                                  { value: "", label: "전체" },
                                  ...GRADE_FILTER_OPTIONS,
                                ]}
                              />
                            )}
                            {columnId === "class" && (
                              <Dropdown
                                value={
                                  (header.column.getFilterValue() as string) ?? ""
                                }
                                onChange={(value) =>
                                  header.column.setFilterValue(value || undefined)
                                }
                                options={[
                                  { value: "", label: "전체" },
                                  ...classList.map((c) => ({ value: c, label: c })),
                                ]}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {pagedRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={emptyRowStyle}>
                  출결 이력이 없습니다.
                </td>
              </tr>
            ) : (
              pagedRows.map((row) => (
                <tr key={row.id} style={tanstackTrStyle}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={getTanstackTdStyle(false, cell.column.getSize())}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 페이징 */}
      <div
        style={{
          margin: "32px 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "22px",
        }}
      >
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 1}
          style={{
            borderRadius: "10px",
            border: "1px solid #90b8ff",
            background: "#f2f6ff",
            color: "#2466c2",
            padding: "6px 18px",
            fontWeight: "bold",
            fontSize: "17px",
            cursor: "pointer",
            minWidth: "70px",
          }}
        >
          이전
        </button>
        <span style={{ fontWeight: "bold", fontSize: "18px" }}>
          페이지 {page} / {totalPages}
        </span>
        <button
          onClick={() => setPage(page + 1)}
          disabled={page === totalPages || totalPages === 0}
          style={{
            borderRadius: "10px",
            border: "1px solid #90b8ff",
            background: "#f2f6ff",
            color: "#2466c2",
            padding: "6px 18px",
            fontWeight: "bold",
            fontSize: "17px",
            cursor: "pointer",
            minWidth: "70px",
          }}
        >
          다음
        </button>
      </div>
    </PageLayout>
  );
}

export default AttendancePage;
